package com.fantasytenis.shared.domain.bracket.usecases

import com.fantasytenis.shared.core.network.ApiResponse
import com.fantasytenis.shared.data.bracket.api.BracketApi
import com.fantasytenis.shared.data.bracket.dto.AdminDeleteBracketEntryRequest
import com.fantasytenis.shared.data.bracket.dto.AdminDeleteBracketEntryResponse
import com.fantasytenis.shared.data.bracket.dto.SetLockOverrideRequest
import com.fantasytenis.shared.data.bracket.repository.BracketRepository
import com.fantasytenis.shared.data.bracket.repository.BracketEntryWithPicks
import com.fantasytenis.shared.data.bracket.repository.PickInput
import com.fantasytenis.shared.domain.bracket.entity.*

// ===== TOURNAMENT DRAW USE CASES =====

/**
 * Get a tournament draw by ID.
 */
class GetTournamentDrawUseCase(
    private val bracketRepository: BracketRepository
) {
    suspend operator fun invoke(drawId: String): Result<TournamentDrawEntity> {
        return bracketRepository.getTournamentDraw(drawId)
    }
}

/**
 * List tournament draws with optional filters.
 */
class ListTournamentDrawsUseCase(
    private val bracketRepository: BracketRepository
) {
    suspend operator fun invoke(
        status: Int? = null,
        gender: String? = null,
        limit: Int = 20,
        offset: Int = 0
    ): Result<List<TournamentDrawEntity>> {
        return bracketRepository.listTournamentDraws(status, gender, limit, offset)
    }
}

/**
 * Get draw positions for a tournament.
 */
class GetDrawPositionsUseCase(
    private val bracketRepository: BracketRepository
) {
    suspend operator fun invoke(
        drawId: String,
        round: Int? = null
    ): Result<List<DrawPositionEntity>> {
        return bracketRepository.getDrawPositions(drawId, round)
    }
}

// ===== BRACKET CHALLENGE USE CASES =====

/**
 * Get a bracket challenge by ID.
 */
class GetBracketChallengeUseCase(
    private val bracketRepository: BracketRepository
) {
    suspend operator fun invoke(challengeId: String): Result<BracketChallengeEntity> {
        return bracketRepository.getBracketChallenge(challengeId)
    }
}

/**
 * List available bracket challenges.
 */
class ListBracketChallengesUseCase(
    private val bracketRepository: BracketRepository
) {
    suspend operator fun invoke(
        status: Int? = null,
        featuredOnly: Boolean = false,
        limit: Int = 20,
        offset: Int = 0
    ): Result<List<BracketChallengeEntity>> {
        return bracketRepository.listBracketChallenges(status, featuredOnly, limit, offset)
    }
}

// ===== BRACKET ENTRY USE CASES =====

/**
 * Create a new bracket entry (join a challenge).
 */
class CreateBracketEntryUseCase(
    private val bracketRepository: BracketRepository
) {
    suspend operator fun invoke(
        challengeId: String,
        bracketName: String? = null
    ): Result<BracketEntryEntity> {
        return bracketRepository.createBracketEntry(challengeId, bracketName)
    }
}

/**
 * Get a bracket entry with all its picks.
 */
class GetBracketEntryUseCase(
    private val bracketRepository: BracketRepository
) {
    suspend operator fun invoke(entryId: String): Result<BracketEntryWithPicks> {
        return bracketRepository.getBracketEntry(entryId)
    }
}

/**
 * Get current user's bracket entries.
 */
class GetUserBracketEntriesUseCase(
    private val bracketRepository: BracketRepository
) {
    suspend operator fun invoke(challengeId: String? = null): Result<List<BracketEntryEntity>> {
        return bracketRepository.getUserBracketEntries(challengeId)
    }
}

/**
 * Submit bracket picks for an entry.
 */
class SubmitBracketPicksUseCase(
    private val bracketRepository: BracketRepository
) {
    suspend operator fun invoke(
        entryId: String,
        picks: List<PickInput>
    ): Result<BracketEntryEntity> {
        return bracketRepository.submitBracketPicks(entryId, picks)
    }
}

/**
 * Cancel a bracket entry.
 */
class CancelBracketEntryUseCase(
    private val bracketRepository: BracketRepository
) {
    suspend operator fun invoke(entryId: String): Result<BracketEntryEntity> {
        return bracketRepository.cancelBracketEntry(entryId)
    }
}

// ===== LEADERBOARD USE CASES =====

/**
 * Get bracket challenge leaderboard.
 */
class GetBracketLeaderboardUseCase(
    private val bracketRepository: BracketRepository
) {
    suspend operator fun invoke(
        challengeId: String,
        limit: Int = 100,
        offset: Int = 0
    ): Result<List<BracketEntryEntity>> {
        return bracketRepository.getBracketLeaderboard(challengeId, limit, offset)
    }
}

// ===== COMPOSITE USE CASES =====

/**
 * Get full bracket data including draw, positions, and challenge details.
 */
class GetFullBracketUseCase(
    private val bracketRepository: BracketRepository
) {
    suspend operator fun invoke(
        challengeId: String
    ): Result<FullBracketData> {
        // Get challenge details
        val challengeResult = bracketRepository.getBracketChallenge(challengeId)
        val challenge = challengeResult.getOrElse { return Result.failure(it) }

        // Get draw details
        val drawResult = bracketRepository.getTournamentDraw(challenge.drawId)
        val draw = drawResult.getOrElse { return Result.failure(it) }

        // Get all positions
        val positionsResult = bracketRepository.getDrawPositions(challenge.drawId)
        val positions = positionsResult.getOrElse { return Result.failure(it) }

        return Result.success(
            FullBracketData(
                challenge = challenge,
                draw = draw,
                positions = positions,
                rounds = buildRounds(positions, draw.drawSize)
            )
        )
    }

    private fun buildRounds(
        positions: List<DrawPositionEntity>,
        drawSize: Int
    ): List<BracketRoundEntity> {
        val roundsNeeded = RoundName.getRoundsForDrawSize(drawSize)

        return roundsNeeded.map { roundName ->
            val roundPositions = positions.filter { it.round == roundName }
            BracketRoundEntity(
                name = roundName,
                matches = buildMatches(roundPositions, roundName)
            )
        }
    }

    private fun buildMatches(
        positions: List<DrawPositionEntity>,
        round: RoundName
    ): List<BracketMatchEntity> {
        // Group positions into matches (pairs)
        return positions
            .sortedBy { it.position }
            .chunked(2)
            .mapIndexed { index, pair ->
                if (pair.size == 2) {
                    BracketMatchEntity(
                        id = pair[0].matchId ?: "${round.name}_${index + 1}",
                        matchNumber = index + 1,
                        round = round,
                        player1 = pair[0],
                        player2 = pair[1],
                        winnerId = pair[0].winnerId ?: pair[1].winnerId,
                        score = pair[0].score ?: pair[1].score,
                        status = determineMatchStatus(pair[0], pair[1]),
                        scheduledTime = null
                    )
                } else {
                    // Single position - create TBD placeholder for missing opponent
                    // This prevents the same position appearing on both sides of a match
                    val placeholder = DrawPositionEntity(
                        id = "${pair[0].id}_placeholder",
                        drawId = pair[0].drawId,
                        round = round,
                        position = pair[0].position + 1,
                        matchNumber = pair[0].matchNumber,
                        playerId = null,
                        playerName = null,
                        playerCountry = null,
                        seed = null,
                        isBye = false,
                        isQualifier = false,
                        isWildcard = false,
                        isLuckyLoser = false,
                        matchId = pair[0].matchId,
                        opponentPositionId = pair[0].id,
                        winnerId = null,
                        matchCompleted = false,
                        score = null
                    )
                    BracketMatchEntity(
                        id = pair[0].matchId ?: "${round.name}_${index + 1}",
                        matchNumber = index + 1,
                        round = round,
                        player1 = pair[0],
                        player2 = placeholder,
                        winnerId = pair[0].winnerId,
                        score = pair[0].score,
                        status = "not_started",
                        scheduledTime = null
                    )
                }
            }
    }

    private fun determineMatchStatus(pos1: DrawPositionEntity, pos2: DrawPositionEntity): String {
        return when {
            pos1.matchCompleted || pos2.matchCompleted -> "ended"
            pos1.matchId != null -> "live" // Has a match ID but not completed
            else -> "not_started"
        }
    }
}

/**
 * Complete bracket data including all related entities.
 */
data class FullBracketData(
    val challenge: BracketChallengeEntity,
    val draw: TournamentDrawEntity,
    val positions: List<DrawPositionEntity>,
    val rounds: List<BracketRoundEntity>
)

// ===== ADMIN USE CASES =====

/**
 * Set lock override for a bracket challenge (SuperAdmin only).
 * Allows admins to manually unlock/lock challenges regardless of time-based lock.
 */
class SetLockOverrideUseCase(
    private val bracketApi: BracketApi
) {
    /**
     * @param challengeId The ID of the bracket challenge
     * @param lockOverride The lock override value (1=NONE, 2=FORCE_UNLOCKED, 3=FORCE_LOCKED)
     * @param reason Optional reason for the override
     * @return The updated BracketChallengeEntity
     */
    suspend operator fun invoke(
        challengeId: String,
        lockOverride: LockOverride,
        reason: String? = null
    ): Result<BracketChallengeEntity> {
        val apiValue = when (lockOverride) {
            LockOverride.NONE -> 1
            LockOverride.FORCE_UNLOCKED -> 2
            LockOverride.FORCE_LOCKED -> 3
        }

        val request = SetLockOverrideRequest(
            lockOverride = apiValue,
            reason = reason
        )

        return when (val response = bracketApi.setLockOverride(challengeId, request)) {
            is ApiResponse.Success -> {
                val dto = response.data
                Result.success(
                    BracketChallengeEntity(
                        id = dto.id,
                        name = dto.name,
                        description = dto.description,
                        drawId = dto.drawId,
                        tournamentName = dto.tournamentName,
                        entryFee = dto.entryFee,
                        prizePool = dto.prizePool,
                        maxEntries = dto.maxEntries,
                        currentEntries = dto.currentEntries,
                        maxEntriesPerUser = dto.maxEntriesPerUser,
                        lockTime = dto.lockTime,
                        status = BracketChallengeStatus.fromApiValue(dto.status),
                        scoringRules = ScoringRulesEntity.DEFAULT, // Simplified - full parsing not needed for admin response
                        payoutStructure = emptyList(),
                        isFeatured = dto.isFeatured,
                        createdAt = dto.createdAt,
                        lockOverride = LockOverride.fromApiValue(dto.lockOverride),
                        lockOverrideBy = dto.lockOverrideBy.ifBlank { null },
                        lockOverrideAt = dto.lockOverrideAt.ifBlank { null },
                        lockOverrideReason = dto.lockOverrideReason.ifBlank { null }
                    )
                )
            }
            is ApiResponse.Error -> Result.failure(Exception(response.message))
        }
    }
}

/**
 * Admin delete a bracket entry (SuperAdmin only).
 * This allows superadmins to remove any entry from a bracket challenge.
 */
class AdminDeleteBracketEntryUseCase(
    private val bracketApi: BracketApi
) {
    /**
     * @param entryId The ID of the entry to delete
     * @param reason The reason for deletion (required for audit)
     * @return The admin delete response
     */
    suspend operator fun invoke(
        entryId: String,
        reason: String
    ): Result<AdminDeleteBracketEntryResponse> {
        val request = AdminDeleteBracketEntryRequest(reason = reason)

        return when (val response = bracketApi.adminDeleteBracketEntry(entryId, request)) {
            is ApiResponse.Success -> Result.success(response.data)
            is ApiResponse.Error -> Result.failure(Exception(response.message))
        }
    }
}
